import {
  BehaviorSubject,
  Observer,
  Subject,
  Subscription,
  from,
  mergeScan,
} from "rxjs";
import {
  SearchFlights,
  SearchFlightsOpts,
  SeatClass,
} from "../models/searchflight";
import { SearchResults } from "../models/results";
import { ILogger, createLogger } from "@/utils/logger";
import { Airport } from "../models/airport";
import { Airline } from "../models/airline";
import {
  ISearchService,
  searchService as defaultSearchService,
} from "../services/search.service";
import {
  IAirportService,
  defaultAirportService,
} from "../services/airport.service";
import {
  IAirlineService,
  defaultAirlineService,
} from "../services/airline.service";
import { ResponseStatusMessage } from "@/core/services/search.service.responses";

export interface SearchFlightState {
  searchFlight: SearchFlights;
  originAirports: Airport[];
  destinationAirports: Airport[];
  airlines: Airline[];
  loading: boolean;
  loadingAirlines: boolean;
  loadingOriginAirports: boolean;
  loadingDestinationAirports: boolean;
  submitted: boolean;
  displayAircraftDiagram: boolean;
  results: SearchResults;
  error: ResponseStatusMessage;
}

const initialSearchFlightState = {
  basicSearch: true,
  seatClass: SeatClass.Economy,
  numberOfFlights: 1,
  numberOfPassengers: 1,
};

const initialState: SearchFlightState = {
  searchFlight: new SearchFlights(initialSearchFlightState),
  originAirports: [],
  destinationAirports: [],
  airlines: [],
  loading: false,
  loadingAirlines: false,
  loadingOriginAirports: false,
  loadingDestinationAirports: false,
  submitted: false,
  displayAircraftDiagram: false,
  results: new SearchResults(),
  error: undefined,
};

export enum ActionTypes {
  SEARCH_FLIGHTS = "SEARCH_FLIGHTS",
  UPDATE_SEARCH_FLIGHTS = "UPDATE_SEARCH_FLIGHTS",
  SEARCH_ORIGIN_AIRPORT = "SEARCH_ORIGIN_AIRPORT",
  SEARCH_DESTINATION_AIRPORT = "SEARCH_DESTINATION_AIRPORT",
  SEARCH_AIRLINES = "SEARCH_AIRLINES",
  RESET = "RESET",
  SWITCH_RESULTS_TAB = "SWITCH_RESULTS_TAB",
  HIDE_MODAL = "HIDE_MODAL",
}

interface IAction<T> {
  type: ActionTypes;
  payload?: T;
}

interface SearchAction extends IAction<SearchFlights> {
  type: ActionTypes.SEARCH_FLIGHTS;
}

interface SearchOriginAirportAction extends IAction<string> {
  type: ActionTypes.SEARCH_ORIGIN_AIRPORT;
}

interface SearchDestinationAirportAction extends IAction<string> {
  type: ActionTypes.SEARCH_DESTINATION_AIRPORT;
}

interface SearchAirlineAction extends IAction<string> {
  type: ActionTypes.SEARCH_AIRLINES;
}

interface SearchFlightsAction extends IAction<never> {
  type: ActionTypes.SEARCH_FLIGHTS;
}

interface UpdateSearchFlightsAction
  extends IAction<Partial<SearchFlightsOpts>> {
  type: ActionTypes.UPDATE_SEARCH_FLIGHTS;
}

interface ResetFormAction extends IAction<SearchFlights> {
  type: ActionTypes.RESET;
}

interface SwitchResultsTabAction extends IAction<never> {
  type: ActionTypes.SWITCH_RESULTS_TAB;
}
interface HideModalAction extends IAction<Partial<SearchFlightsOpts>> {
  type: ActionTypes.HIDE_MODAL;
}

type Action =
  | SearchAction
  | SearchOriginAirportAction
  | SearchDestinationAirportAction
  | SearchAirlineAction
  | SearchFlightsAction
  | UpdateSearchFlightsAction
  | ResetFormAction
  | SwitchResultsTabAction
  | HideModalAction;

export class SearchFlightsStore {
  private readonly _state: BehaviorSubject<SearchFlightState>;

  private actionSubject = new Subject<IAction<any>>();

  constructor(
    initial: SearchFlightState = initialState,
    private readonly _searchService: ISearchService = defaultSearchService,
    private readonly _airportService: IAirportService = defaultAirportService,
    private readonly _airlineService: IAirlineService = defaultAirlineService,
    private readonly _logger: ILogger = createLogger("SearchFlightsStore"),
  ) {
    this._state = new BehaviorSubject<SearchFlightState>(initial);
    this.actionSubject
      .pipe(
        mergeScan(
          (acc: SearchFlightState, value: Action) =>
            from(this.handleAction(acc, value)),
          this._state.getValue(),
        ),
      )
      .subscribe((newState: SearchFlightState) => {
        this._state.next(newState);
      });
  }

  public async handleAction(
    state: SearchFlightState,
    action: Action,
  ): Promise<SearchFlightState> {
    const newState: Partial<SearchFlightState> = {};
    switch (action.type) {
      case ActionTypes.SEARCH_FLIGHTS:
        this.setLoading("loading");
        const searchResultState = await this.searchFlights(state.searchFlight);
        Object.assign(newState, searchResultState);
        break;
      case ActionTypes.UPDATE_SEARCH_FLIGHTS:
        newState.searchFlight = this.updateSearchFlights(
          state.searchFlight,
          action.payload,
        );
        break;
      case ActionTypes.RESET:
        Object.assign(newState, initialState);
        break;
      case ActionTypes.SEARCH_ORIGIN_AIRPORT:
        this.setLoading("loadingOriginAirports");
        newState.originAirports = await this.searchAirport(action.payload);
        newState.loadingOriginAirports = false;
        break;
      case ActionTypes.SEARCH_DESTINATION_AIRPORT:
        this.setLoading("loadingDestinationAirports");
        newState.destinationAirports = await this.searchAirport(action.payload);
        newState.loadingDestinationAirports = false;
        break;
      case ActionTypes.SEARCH_AIRLINES:
        this.setLoading("loadingAirlines");
        newState.airlines = await this.searchAirline(action.payload);
        newState.loadingAirlines = false;
        break;
      case ActionTypes.SWITCH_RESULTS_TAB:
        newState.displayAircraftDiagram = !state.displayAircraftDiagram;
        break;
      case ActionTypes.HIDE_MODAL:
        Object.assign(newState, initialState, {
          searchFlight: new SearchFlights({
            ...initialSearchFlightState,
            ...action.payload,
          }),
          error: undefined,
        });
        break;
    }
    return {
      ...state,
      ...newState,
    };
  }

  public subscribe(
    observerOrNext?:
      | Partial<Observer<SearchFlightState>>
      | ((value: SearchFlightState) => void),
  ): Subscription {
    return this._state.subscribe(observerOrNext);
  }

  public dispatch(action: Action) {
    this.actionSubject.next(action);
  }

  // actions

  private setLoading(
    property:
      | "loading"
      | "loadingOriginAirports"
      | "loadingDestinationAirports"
      | "loadingAirlines",
  ): void {
    const currentState = { ...this._state.getValue() };
    currentState[property] = true;
    this._state.next(currentState);
  }

  private updateSearchFlights(
    state: SearchFlights,
    update: Partial<SearchFlightsOpts>,
  ): SearchFlights {
    return new SearchFlights({
      ...state.toOpts(),
      ...update,
    }).sanitize();
  }

  private async searchAirport(query: string): Promise<Airport[]> {
    const airports = await this._airportService.search(query);
    return airports.map((airport) => ({
      name: airport.name,
      code: airport.iataCode,
    }));
  }

  private async searchAirline(query: string): Promise<Airline[]> {
    const airlines = await this._airlineService.search(query);
    return airlines.map((airline) => ({
      name: airline.name,
      code: airline.icaoCode,
    }));
  }

  private async searchFlights(
    searchFlights: SearchFlights,
  ): Promise<Partial<SearchFlightState>> {
    try {
      const results = await this._searchService.search(searchFlights);
      if (results.errorMessage) {
        return {
          submitted: true,
          loading: false,
          error: results.errorMessage,
        };
      }
      const emissionsPerPassenger = results.seatClasses.find(
        (x) => x.type === searchFlights.seatClass,
      ).emissionsPerSeat;
      return {
        submitted: true,
        loading: false,
        airlines: [{ name: results.airline.name, code: results.airline.code }],
        originAirports: [
          {
            name: results.originAirport.name,
            code: results.originAirport.code,
          },
        ],
        destinationAirports: [
          {
            name: results.destinationAirport.name,
            code: results.destinationAirport.code,
          },
        ],
        searchFlight: new SearchFlights({
          ...searchFlights.toOpts(),
          airline: results.airline.code,
          arrivalAirport: results.destinationAirport.code,
          departureAirport: results.originAirport.code,
          basicSearch: false,
          flightNumber: results.flightNumber,
        }),
        results: new SearchResults({
          aircraftTypeName: results.aircraftTypeName,
          distance: results.distance,
          emissionsPerPassenger: emissionsPerPassenger,
          emissionsTotal:
            emissionsPerPassenger *
            searchFlights.numberOfFlights *
            searchFlights.numberOfPassengers,
          flightsOver12Months: results.flightsOver12Months,
          aircraftRegistration: results.aircraftRegistration,
          seatClasses: results.seatClasses.map((seatClassResult) => {
            return {
              name: seatClassResult.name,
              emissionsPerSeat: seatClassResult.emissionsPerSeat,
              seats: seatClassResult.seats,
            };
          }),
        }),
      };
    } catch (err) {
      this._logger.error(err);
      return {
        error: err,
      };
    }
  }
}

const searchFlightStore = new SearchFlightsStore();
export { searchFlightStore };

const dispatch = (action: Action) => searchFlightStore.dispatch(action);
export { dispatch };
